import { useCallback, useEffect, useRef, useState, Fragment } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TabOutlinedIcon from "@mui/icons-material/TabOutlined";

import FileDropzone from "../../file-dropzone";
import TakeDocPic from "../../take-doc-pic";
import ConfirmDialog from "../../confirmation";
import TextFieldPrompt from "../../text-field-prompt";

import { SkillTemplate } from "../../../../src/API";
import { useSkillTemplates } from "../../../../providers/state/skillTemplate";
import { useSnackbar } from "notistack";
import { useProfile } from "../../../../providers/profile";
import { useAuth0 } from "@auth0/auth0-react";
import { idFetcher } from "../../../../utilities/fetcher";

import axios from "axios";
import { v4 as uuidv4 } from "uuid";

interface Doc {
  title: string;
  when: number;
  url: string;
}

const SupportingDocuments = ({
  template,
  refresh,
  editable,
}: {
  template: Partial<SkillTemplate>;
  refresh: () => Promise<void>;
  editable: boolean;
}) => {
  const [supportingDocuments, setSupportingDocuments] = useState<Doc[]>([]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editDoc, setEditDoc] = useState<Doc>();
  const [editDocName, setEditDocName] = useState("");
  const [editDocNameOpen, setEditDocNameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteAction = useRef<() => void>();

  const { upsertSkillTemplate } = useSkillTemplates();

  const { getIdTokenClaims } = useAuth0();
  const { account } = useProfile();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setSupportingDocuments(JSON.parse(template.supportingDocuments || "[]"));
  }, [template]);

  const openDoc = ({ url }: { url: string }) => {
    if (url.startsWith("https://") || url.startsWith("http://")) {
      return window.open(url, "_blank", "noreferrer");
    }

    const winRef = window.open(undefined, "_blank");
    (async () => {
      const urlReq = await idFetcher(`/api/s3/get/${url}`, getIdTokenClaims);
      if (!urlReq) {
        enqueueSnackbar(
          "Problem with opening supporting document. Please try again.",
          {
            variant: "error",
          }
        );
        return;
      }
      if (winRef) {
        winRef.location = urlReq.signedUrl;
      } else {
        window.open(urlReq.signedUrl, "_blank");
      }
    })();
  };

  const onDocDrop = useCallback(
    async (acceptedFiles: any[]) => {
      if (!account) return;

      acceptedFiles.forEach(async (file: any) => {
        try {
          const ext = file.name.split(".").pop();
          const key = encodeURIComponent(
            `protected/${account.sub}/${uuidv4()}.${ext}`
          );
          // console.log(key);
          const upload = await idFetcher(
            `/api/s3/put/${key}`,
            getIdTokenClaims
          );
          // console.log(upload.signedUrl);

          const uploadResult = await axios.put(upload.signedUrl, file, {
            headers: { "content-type": file.type },
            onUploadProgress: (evt) => {
              if (!evt.total) return setUploadProgress(0);
              setUploadProgress((evt.loaded / evt.total) * 100);
            },
          });
          // console.log(uploadResult);

          const add = {
            title: file.name,
            when: new Date().valueOf(),
            url: key,
          };
          const result = await upsertSkillTemplate({
            id: template.id,
            supportingDocuments: JSON.stringify([...supportingDocuments, add]),
          });

          await refresh();
          return result;
        } catch (e) {
          console.error(e);
          enqueueSnackbar("Problem with file upload. Please try again.", {
            variant: "error",
          });
        }
      });
    },
    [
      account,
      template,
      enqueueSnackbar,
      getIdTokenClaims,
      refresh,
      supportingDocuments,
      upsertSkillTemplate,
    ]
  );

  const addScannedDoc = useCallback(
    async ({ key }: { key: string }) => {
      const now = new Date();
      const add = {
        title: `Picture ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`,
        when: now.valueOf(),
        url: key,
      };
      const result = await upsertSkillTemplate({
        id: template.id,
        supportingDocuments: JSON.stringify([...supportingDocuments, add]),
      });

      await refresh();
      return result;
    },
    [upsertSkillTemplate, template, supportingDocuments, refresh]
  );

  const deleteDoc = useCallback(
    async (doc: any) => {
      const result = await upsertSkillTemplate({
        id: template.id,
        supportingDocuments: JSON.stringify(
          supportingDocuments.filter((d) => d !== doc)
        ),
      });

      await refresh();
      return result;
    },
    [upsertSkillTemplate, template, supportingDocuments, refresh]
  );

  const openEditDocName = useCallback((doc: any) => {
    setEditDoc(doc);
    setEditDocName(doc.title);
    setEditDocNameOpen(true);
  }, []);

  const saveEditDocName = useCallback(async () => {
    if (!editDoc) return setEditDocNameOpen(false);

    const foundVid = supportingDocuments.find(
      (item) => item.when === editDoc.when
    );
    if (foundVid) {
      foundVid.title = editDocName;

      const result = await upsertSkillTemplate({
        id: template.id,
        supportingDocuments: JSON.stringify(supportingDocuments),
      });

      await refresh();
      setEditDocNameOpen(false);
      return result;
    }
  }, [
    editDoc,
    supportingDocuments,
    editDocName,
    upsertSkillTemplate,
    template,
    refresh,
  ]);

  const handleDelete = useCallback(() => {
    if (deleteAction.current) deleteAction.current();
    deleteAction.current = undefined;
  }, []);

  return (
    <>
      <Card>
        <CardHeader
          action={
            editable ? (
              <Button
                endIcon={<AddAPhotoOutlinedIcon />}
                color="success"
                size="small"
                variant="contained"
                onClick={() => setSelectorOpen(true)}
              >
                Take Photo
              </Button>
            ) : null
          }
          title="SUPPORTING DOCUMENTS"
          sx={{ "& span": { fontSize: "1rem" } }}
        />
        <CardContent>
          <List dense={true}>
            {supportingDocuments.map((doc) => (
              <Fragment key={doc.url}>
                <Divider />
                <ListItem sx={{ gap: "0.5rem" }}>
                  <ListItemText
                    sx={{ "& span": { fontWeight: 700 } }}
                    primary={doc.title}
                  />
                  <Box sx={{ float: "right", display: "flex" }}>
                    <Button
                      onClick={() => {
                        openDoc(doc);
                      }}
                      aria-label="open"
                      color="secondary"
                      endIcon={<TabOutlinedIcon />}
                      size="small"
                      variant="outlined"
                    >
                      Open
                    </Button>
                    {editable ? (
                      <>
                        <IconButton
                          onClick={() => openEditDocName(doc)}
                          aria-label="edit"
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            deleteAction.current = () => deleteDoc(doc);
                            setDeleteOpen(true);
                          }}
                          aria-label="delete"
                          size="small"
                        >
                          <DeleteForeverOutlinedIcon />
                        </IconButton>
                      </>
                    ) : null}
                  </Box>
                </ListItem>
              </Fragment>
            ))}
          </List>
          {editable ? (
            <>
              <FileDropzone onDrop={onDocDrop} />
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: "8px", marginTop: "1rem" }}
              />
            </>
          ) : null}
        </CardContent>
      </Card>
      <TakeDocPic
        open={selectorOpen}
        setOpen={setSelectorOpen}
        onSave={addScannedDoc}
        isPrivate={false}
      />
      <TextFieldPrompt
        open={editDocNameOpen}
        setOpen={setEditDocNameOpen}
        value={editDocName}
        setValue={setEditDocName}
        onSave={saveEditDocName}
      />
      <ConfirmDialog
        title="Delete Supporting Document?"
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDelete}
      >
        Are you sure you want to delete?
      </ConfirmDialog>
    </>
  );
};

export default SupportingDocuments;
